/**
 * QualityChipStrip — Sprint A1 (updated)
 *
 * Compression rules:
 *   Compliant → ALWAYS compress to CompliantOrb
 *   Due       → compress only when count ≥ 5 (DueOrb)
 *   Overdue   → NEVER compress
 *   Staged    → NEVER compress
 *   Pending   → NEVER compress
 *
 * Individual chips for: overdue, staged, pending, and due (when < 5)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ShieldCheck, Clock, AlertCircle, CheckCircle2, HelpCircle } from 'lucide-react';
import { CompliantOrb } from './CompliantOrb';
import { DueOrb, DUE_COMPRESS_THRESHOLD } from './DueOrb';

const CHIP_STYLES = {
  due:                  { bg: 'bg-amber-100 border-amber-300 text-amber-800',   dot: 'bg-amber-500' },
  overdue:              { bg: 'bg-red-100 border-red-300 text-red-800',         dot: 'bg-red-500' },
  staged:               { bg: 'bg-blue-100 border-blue-300 text-blue-800',      dot: 'bg-blue-500' },
  pending_confirmation: { bg: 'bg-violet-100 border-violet-300 text-violet-800',dot: 'bg-violet-500' },
};

const STATUS_ICON = {
  due:                  <Clock className="w-3 h-3 text-amber-500" />,
  overdue:              <AlertCircle className="w-3 h-3 text-red-500" />,
  staged:               <Clock className="w-3 h-3 text-blue-500" />,
  pending_confirmation: <HelpCircle className="w-3 h-3 text-violet-500" />,
};

const STATUS_TOOLTIP = {
  due:                  'Due — not yet completed this period',
  overdue:              'Overdue — action needed',
  staged:               'In note / ordered this visit — awaiting confirmation or result',
  pending_confirmation: 'Patient response detected — confirm at finalization',
};

// ── Individual chip (for non-compliant, non-compressed) ───────────────────

function QualityChip({ chip, onChipClick }) {
  const [pulsing, setPulsing] = useState(false);
  const prevStatus = useRef(chip.status);

  useEffect(() => {
    if (prevStatus.current !== chip.status) {
      setPulsing(true);
      const t = setTimeout(() => setPulsing(false), 1000);
      prevStatus.current = chip.status;
      return () => clearTimeout(t);
    }
  }, [chip.status]);

  const style = CHIP_STYLES[chip.status] || CHIP_STYLES.due;
  const icon = STATUS_ICON[chip.status];

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onChipClick && onChipClick(chip)}
            className={`
              inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border
              text-[10px] font-semibold transition-all duration-300 cursor-pointer select-none
              hover:shadow-sm hover:scale-105 active:scale-95
              ${style.bg}
              ${pulsing ? 'animate-pulse' : ''}
            `}
            data-testid={`quality-chip-${chip.measure_id}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dot}`} />
            {icon}
            <span className="truncate max-w-[80px]">{chip.short_label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[220px] text-xs">
          <p className="font-semibold">{chip.short_label}</p>
          <p className="text-muted-foreground">{STATUS_TOOLTIP[chip.status] || chip.status}</p>
          {chip.expiration?.expiration_label && (
            <p className="text-muted-foreground mt-0.5">{chip.expiration.expiration_label}</p>
          )}
          {chip.evidence_this_visit?.length > 0 && (
            <p className="text-emerald-600 mt-0.5">✓ Captured this visit</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export function QualityChipStrip({
  patientId,
  encounterId,
  context = 'chart',
  density = 'compact',
  onChipClick,
  onStateUpdate,
  className = '',
}) {
  const [chips, setChips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      let data;
      if (encounterId) {
        const res = await api.get(`/encounters/${encounterId}/quality-chip-state`);
        data = res.data;
      } else if (patientId) {
        const res = await api.post(`/patients/${patientId}/quality-chip-state`, {});
        data = res.data;
      } else return;

      setChips(data.chips || []);
      setSummary(data.summary);
      if (onStateUpdate) onStateUpdate(data);
    } catch {
      // Silent fail — quality strip is ambient
    } finally {
      setLoading(false);
    }
  }, [encounterId, patientId, onStateUpdate]);

  useEffect(() => { fetchState(); }, [fetchState]);

  // 10s polling during live encounter
  useEffect(() => {
    if (!encounterId || context !== 'live_visit') return;
    pollRef.current = setInterval(fetchState, 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [encounterId, context, fetchState]);

  if (loading) return (
    <div className={`flex items-center gap-1.5 h-7 ${className}`}>
      {[1,2,3].map(i => <div key={i} className="h-6 w-20 bg-muted/50 rounded-full animate-pulse" />)}
    </div>
  );

  if (chips.length === 0) return null;

  // Categorize chips
  const compliantChips = chips.filter(c => c.status === 'compliant');
  const dueChips       = chips.filter(c => c.status === 'due');
  const overdueChips   = chips.filter(c => c.status === 'overdue');
  const stagedChips    = chips.filter(c => c.status === 'staged');
  const pendingChips   = chips.filter(c => c.status === 'pending_confirmation');

  // Fresh captures count (drives CompliantOrb pulse)
  const freshCount = compliantChips.filter(c => c.captured_this_encounter).length;
  const isLiveEncounter = context === 'live_visit' && !!encounterId;
  const hasUrgent = overdueChips.length > 0 || pendingChips.length > 0;

  return (
    <div
      className={`flex items-center gap-1.5 flex-wrap ${className}`}
      data-testid="quality-chip-strip">

      {/* Anchor shield icon */}
      <ShieldCheck className={`w-3.5 h-3.5 shrink-0 ${hasUrgent ? 'text-red-400' : compliantChips.length > 0 ? 'text-emerald-400' : 'text-amber-400'}`} />

      {/* 1. Compliant — ALWAYS compressed */}
      {compliantChips.length > 0 && (
        <CompliantOrb
          count={compliantChips.length}
          freshCount={freshCount}
          isLiveEncounter={isLiveEncounter}
          chips={compliantChips}
        />
      )}

      {/* 2. Due — compress only at ≥5, else individual chips */}
      {dueChips.length >= DUE_COMPRESS_THRESHOLD ? (
        <DueOrb
          count={dueChips.length}
          chips={dueChips}
          isLiveEncounter={isLiveEncounter}
          encounterId={encounterId}
          onAction={(chip, actionId) => onChipClick && onChipClick({ ...chip, _actionId: actionId })}
        />
      ) : (
        dueChips.map(chip => (
          <QualityChip key={chip.measure_id} chip={chip} onChipClick={onChipClick} />
        ))
      )}

      {/* 3. Overdue — NEVER compressed */}
      {overdueChips.map(chip => (
        <QualityChip key={chip.measure_id} chip={chip} onChipClick={onChipClick} />
      ))}

      {/* 4. Staged — NEVER compressed */}
      {stagedChips.map(chip => (
        <QualityChip key={chip.measure_id} chip={chip} onChipClick={onChipClick} />
      ))}

      {/* 5. Pending confirmation — NEVER compressed */}
      {pendingChips.map(chip => (
        <QualityChip key={chip.measure_id} chip={chip} onChipClick={onChipClick} />
      ))}

      {/* All clear */}
      {chips.length > 0 && overdueChips.length === 0 && pendingChips.length === 0 &&
       stagedChips.length === 0 && dueChips.length === 0 && (
        <span className="text-[9px] text-emerald-600 font-medium">All clear</span>
      )}
    </div>
  );
}

export { QualityChip, CHIP_STYLES };
